import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useApi, type AiProposal } from "@ncp/api-client";
import { formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, InfoHint, SourceText, Textarea, useSave } from "@ncp/ui";

/** The same floor the server applies: shorter is a mention, not an answer. */
const MIN_ANSWER = 25;

interface BriefPoint { number: number; kind: string; point: string; answer?: string | null; answeredBy?: string | null; answeredAt?: string | null }
interface BriefPeer { reviewerName: string; reviewerTitle?: string | null; reviewedAt: string; verdict: string; recommendedOutcome?: string | null; independent: boolean; opinion: string; opinionAr?: string | null }
interface BriefCommittee { recommendation?: string | null; issuedAt?: string | null; session?: string | null; votesFor: number; votesTotal: number; reasons?: string | null; reasonsAr?: string | null; conditions: string[]; departmentRecommendation?: string | null; differsFromDepartment: boolean }
interface BriefSafeguards { reportProposalId?: string | null; peer?: BriefPeer | null; points: BriefPoint[]; committee?: BriefCommittee | null; committeeReferred: boolean; committeeRequired: boolean; committeeApplies?: boolean }

const verdictTone = (v: string) => (v === "Concur" ? "success" : v === "Dissent" ? "danger" : "warning");

/**
 * The safeguards the decision brief carries to the Minister, as the records give them: the independent reviewer's verdict on the approved
 * report, each reservation or dissent point with the Department's answer, and the Committee's opinion as adopted. The case team writes each
 * answer here; the server holds the brief until every point has one (EC-12.05, EC-17.03, AC-08.04).
 */
export function BriefSafeguardsCard({ brief, canApprove, onSaved }: { brief: AiProposal; canApprove: boolean; onSaved: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const structured = (brief.structured ?? {}) as Record<string, unknown> & { safeguards?: BriefSafeguards; suggestedAnswers?: string[] };
  const s = structured.safeguards;
  const pending = brief.status === "Proposed";
  const [answers, setAnswers] = useState<Record<number, string>>(() => Object.fromEntries((s?.points ?? []).map((p) => [p.number, p.answer ?? ""])));
  // Condition 3.10: the brief counts as opened once the end of its text has been on screen on the Decision tab, and its sources once one is
  // opened there – as on the shared review card, never on loading this card.

  const approve = useSave({
    mutationFn: () => api.post<{ succeeded: boolean; error?: string }>(`/api/ai/proposals/${brief.id}/approve`, {
      note: null,
      editedStructuredJson: JSON.stringify({ ...structured, safeguards: { ...s, points: (s?.points ?? []).map((p) => ({ ...p, answer: (answers[p.number] ?? "").trim() })) } }),
    }),
    saved: t("backoffice:briefSafeguards.approved"),
    onSuccess: () => onSaved(),
  });

  if (!s) return null;
  const ar = locale === "ar";
  const editable = pending && canApprove;
  const complete = s.points.every((p) => (answers[p.number] ?? "").trim().length >= MIN_ANSWER);
  const suggestions = structured.suggestedAnswers ?? [];
  const c = s.committee;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <ShieldCheck className="size-4 text-aegold-700" /> {t("backoffice:briefSafeguards.title")}
          <InfoHint>{t("backoffice:briefSafeguards.hint")}</InfoHint>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <section className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:briefSafeguards.peer")}</p>
          {s.peer ? (
            <>
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{s.peer.reviewerName}{s.peer.reviewerTitle ? ` (${s.peer.reviewerTitle})` : ""}</span>
                <Badge size="sm" variant={verdictTone(s.peer.verdict)}>{t(`backoffice:briefSafeguards.verdicts.${s.peer.verdict}`, { defaultValue: s.peer.verdict })}</Badge>
                <span className="text-xs text-muted-foreground">{formatDate(s.peer.reviewedAt, locale)}</span>
                {!s.peer.independent ? <Badge size="sm" variant="warning">{t("backoffice:briefSafeguards.notIndependent")}</Badge> : null}
              </p>
              <p className="whitespace-pre-wrap text-xs text-aeblack-900/80"><SourceText text={(ar && s.peer.opinionAr) || s.peer.opinion} /></p>
            </>
          ) : <p className="text-xs text-muted-foreground">{t("backoffice:briefSafeguards.noPeer")}</p>}
        </section>

        {s.points.length > 0 ? (
          <section className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:briefSafeguards.points", { count: s.points.length })}</p>
            {s.points.map((p, i) => (
              <div key={p.number} className="rounded-xl border border-sand-200 p-3">
                <p className="flex items-start gap-2"><Badge size="sm" variant={p.kind === "Dissent" ? "danger" : "warning"}>{p.number}</Badge><span className="whitespace-pre-wrap"><SourceText text={p.point} /></span></p>
                {editable ? (
                  <div className="mt-2 space-y-1.5">
                    <Field label={t("backoffice:briefSafeguards.answer")} hint={t("backoffice:briefSafeguards.answerHint", { min: MIN_ANSWER })} required>
                      {(id) => <Textarea id={id} rows={3} value={answers[p.number] ?? ""} onChange={(e) => setAnswers((a) => ({ ...a, [p.number]: e.target.value }))} draft={`proposal:${brief.id}:safeguard:${p.number}:answer`} />}
                    </Field>
                    {suggestions[i] ? (
                      <p className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{t("backoffice:briefSafeguards.suggested")}: <SourceText text={suggestions[i]} /></span>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setAnswers((a) => ({ ...a, [p.number]: suggestions[i] }))}>{t("backoffice:briefSafeguards.useSuggestion")}</Button>
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-2 whitespace-pre-wrap text-xs">
                    <span className="font-semibold">{t("backoffice:briefSafeguards.answer")}: </span>
                    {p.answer ? <SourceText text={p.answer} /> : <span className="text-warning-800">{t("backoffice:briefSafeguards.unanswered")}</span>}
                    {p.answeredBy ? <span className="text-muted-foreground"> · {p.answeredBy}{p.answeredAt ? `, ${formatDate(p.answeredAt, locale)}` : ""}</span> : null}
                  </p>
                )}
              </div>
            ))}
          </section>
        ) : null}

        {s.committeeApplies === false ? null : <section className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:briefSafeguards.committee")}</p>
          {c ? (
            <>
              <p className="flex flex-wrap items-center gap-2">
                <Badge size="sm" variant="outline">{t(`backoffice:briefSafeguards.outcomes.${c.recommendation ?? ""}`, { defaultValue: c.recommendation ?? "—" })}</Badge>
                {c.votesTotal > 0 ? <span className="text-xs">{t("backoffice:briefSafeguards.tally", { votesFor: c.votesFor, total: c.votesTotal })}</span> : null}
                {c.issuedAt ? <span className="text-xs text-muted-foreground">{formatDate(c.issuedAt, locale)}{c.session ? ` · ${c.session}` : ""}</span> : null}
                {c.differsFromDepartment ? <Badge size="sm" variant="warning">{t("backoffice:briefSafeguards.differs")}</Badge> : null}
              </p>
              {(ar && c.reasonsAr) || c.reasons ? <blockquote className="border-s-2 border-sand-300 ps-3 text-xs italic"><SourceText text={(ar && c.reasonsAr) || c.reasons || ""} /></blockquote> : null}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">{s.committeeReferred ? t("backoffice:briefSafeguards.committeePending") : s.committeeRequired ? t("backoffice:briefSafeguards.notReferredRequired") : t("backoffice:briefSafeguards.notReferred")}</p>
          )}
        </section>}

        {editable ? (
          <div className="flex justify-end">
            <Button variant="gold" size="sm" disabled={!complete} loading={approve.isPending} onClick={() => approve.mutate()}>{t("backoffice:briefSafeguards.approve")}</Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
